/**
 * Agent Hub — Claude.ai-style web UI với:
 *  - SSE streaming (Phase 3.2)
 *  - Rate limit display (Phase 3.4)
 *  - Conversation threads (Phase 3.5): generate conversation_id, group history
 */
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Drawer,
    Chip,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Divider,
    Button,
    Tabs,
    Tab,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    IconRobot,
    IconSend,
    IconChevronDown,
    IconTool,
    IconCircleCheck,
    IconAlertCircle,
    IconLayoutSidebar,
    IconRefresh,
    IconHistory,
    IconUsersGroup,
    IconMessage,
    IconPlus,
    IconClipboard,
    IconX,
    IconEye,
    IconPhoto,
    IconUpload
} from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api/v1' });
api.interceptors.request.use((cfg) => {
    const token = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

const ORCHESTRATOR = 'datahub-orchestrator';
const MAX_WIDTH = 760;
const CONVERSATION_KEY = 'agentHub.currentConversationId';

// Paste shortener thresholds — trên ngưỡng nào → giấu vào chip
const PASTE_THRESHOLD_LINES = 5;
const PASTE_THRESHOLD_CHARS = 250;

// Conversation length limits — giảm lag khi message nhiều
const DEFAULT_VISIBLE_COUNT = 20;    // Hiển thị 20 message gần nhất
const VISIBLE_INCREMENT = 20;        // "Load more older" mỗi lần thêm 20

// State persistence — chống mất tiến trình khi refresh
const STATE_KEY_PREFIX = 'agentHub.state.';
const MAX_PERSISTED_MESSAGES = 50;

const getAuthHeader = () => {
    const t = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    return t ? `Bearer ${t}` : '';
};

const newConvId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    // fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

const relTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'vừa xong';
    if (min < 60) return `${min} phút trước`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} giờ trước`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} ngày trước`;
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ============ Markdown ============
const Md = ({ children }) => (
    <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
            code({ inline, className, children, ...props }) {
                const m = /language-(\w+)/.exec(className || '');
                return !inline && m ? (
                    <SyntaxHighlighter style={vscDarkPlus} language={m[1]} PreTag="div" customStyle={{ borderRadius: 8, fontSize: 13, margin: '12px 0' }} {...props}>
                        {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                ) : (
                    <code style={{ background: '#f1f3f5', padding: '2px 6px', borderRadius: 4, fontSize: '0.92em', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' }} className={className} {...props}>{children}</code>
                );
            },
            p: ({ children }) => <Typography component="p" sx={{ my: 1, lineHeight: 1.7, fontSize: 15 }}>{children}</Typography>,
            h1: ({ children }) => <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>{children}</Typography>,
            h2: ({ children }) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>{children}</Typography>,
            h3: ({ children }) => <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>{children}</Typography>,
            ul: ({ children }) => <Box component="ul" sx={{ pl: 3, my: 1 }}>{children}</Box>,
            ol: ({ children }) => <Box component="ol" sx={{ pl: 3, my: 1 }}>{children}</Box>,
            li: ({ children }) => <Box component="li" sx={{ my: 0.3, lineHeight: 1.7 }}>{children}</Box>,
            table: ({ children }) => <Box sx={{ overflowX: 'auto', my: 1.5 }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>{children}</table></Box>,
            th: ({ children }) => <th style={{ border: '1px solid #e1e4e8', padding: '8px 12px', background: '#f6f8fa', textAlign: 'left' }}>{children}</th>,
            td: ({ children }) => <td style={{ border: '1px solid #e1e4e8', padding: '8px 12px' }}>{children}</td>
        }}
    >{children || ''}</ReactMarkdown>
);

// ============ Tool trace (compact grouped, default collapsed) ============
const _shortTool = (name) => (name || '').replace(/^mcp__\w+__/, '');

const _toolPreview = (input) => {
    if (!input) return '';
    if (input.method && input.path) return `${input.method} ${input.path}`;
    if (input.subagent_type) return `→ ${input.subagent_type}`;
    if (input.description) return String(input.description).slice(0, 60);
    if (input.command) return String(input.command).slice(0, 60);
    if (input.pattern) return `pattern: ${String(input.pattern).slice(0, 50)}`;
    if (input.file_path) {
        const fp = String(input.file_path);
        return fp.length > 50 ? '…' + fp.slice(-50) : fp;
    }
    if (input.query) return `query: ${String(input.query).slice(0, 50)}`;
    return '';
};

const ToolRow = ({ t }) => {
    const [expanded, setExpanded] = useState(false);
    const isPending = t.output_preview === '(pending)';
    return (
        <Box sx={{ borderTop: '1px solid', borderColor: 'grey.100', '&:first-of-type': { borderTop: 'none' } }}>
            <Box
                onClick={() => !isPending && setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    cursor: isPending ? 'default' : 'pointer',
                    '&:hover': isPending ? {} : { bgcolor: 'grey.50' }
                }}
            >
                {isPending ? <CircularProgress size={11} thickness={5} />
                 : t.success ? <IconCircleCheck size={13} color="#2e7d32" />
                 : <IconAlertCircle size={13} color="#d32f2f" />}
                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12, color: isPending ? 'text.secondary' : 'text.primary' }}>
                    {_shortTool(t.tool)}
                </Box>
                <Box component="span" sx={{ flex: 1, color: 'text.secondary', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {_toolPreview(t.input)}
                </Box>
                {t.latency_ms > 0 && (
                    <Box component="span" sx={{ color: 'text.secondary', fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                        {t.latency_ms < 1000 ? `${t.latency_ms}ms` : `${(t.latency_ms / 1000).toFixed(1)}s`}
                    </Box>
                )}
                {!isPending && (
                    <IconChevronDown size={11} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', opacity: 0.5 }} />
                )}
            </Box>
            {expanded && !isPending && (
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#fafbfc' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 10.5 }}>Input</Typography>
                    <Box component="pre" sx={{ m: 0, mt: 0.3, p: 0.75, bgcolor: '#fff', border: '1px solid', borderColor: 'grey.200', borderRadius: 0.5, fontSize: 11, overflow: 'auto', maxHeight: 120 }}>
                        {JSON.stringify(t.input, null, 2)}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 10.5, display: 'block', mt: 0.5 }}>Output</Typography>
                    <Box component="pre" sx={{ m: 0, mt: 0.3, p: 0.75, bgcolor: '#fff', border: '1px solid', borderColor: 'grey.200', borderRadius: 0.5, fontSize: 11, overflow: 'auto', maxHeight: 180 }}>
                        {t.output_preview}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const ToolTrace = ({ trace }) => {
    const [open, setOpen] = useState(false);
    if (!trace?.length) return null;

    // Stats
    const success = trace.filter((t) => t.success && t.output_preview !== '(pending)').length;
    const failed = trace.filter((t) => !t.success && t.output_preview !== '(pending)').length;
    const pending = trace.filter((t) => t.output_preview === '(pending)').length;
    const totalLatency = trace.reduce((s, t) => s + (t.latency_ms || 0), 0);

    // Group by tool name → "Agent×2, Bash×3, call_internal_api×5, Read×2"
    const groups = {};
    trace.forEach((t) => {
        const k = _shortTool(t.tool);
        groups[k] = (groups[k] || 0) + 1;
    });
    const groupEntries = Object.entries(groups).sort((a, b) => b[1] - a[1]);

    const headerColor = failed > 0 ? 'error.light' : pending > 0 ? 'grey.300' : 'success.light';
    const headerBg = failed > 0 ? 'error.50' : pending > 0 ? 'grey.50' : 'success.50';

    return (
        <Box sx={{
            my: 1.5,
            border: '1px solid',
            borderColor: headerColor,
            bgcolor: headerBg,
            borderRadius: 1,
            overflow: 'hidden'
        }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
                }}
            >
                <IconTool size={14} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 12 }}>
                    {trace.length} tool call{trace.length > 1 ? 's' : ''}
                </Typography>
                {pending > 0 && <CircularProgress size={11} thickness={5} />}
                {success > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#2e7d32' }}>
                        <IconCircleCheck size={12} />
                        <Typography variant="caption" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{success}</Typography>
                    </Box>
                )}
                {failed > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#d32f2f' }}>
                        <IconAlertCircle size={12} />
                        <Typography variant="caption" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{failed}</Typography>
                    </Box>
                )}
                <Box sx={{ flex: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 0, overflow: 'hidden' }}>
                    {groupEntries.slice(0, 6).map(([k, n]) => (
                        <Box key={k} component="span" sx={{ fontSize: 10.5, fontFamily: 'monospace', color: 'text.secondary', bgcolor: '#fff', border: '1px solid', borderColor: 'grey.300', borderRadius: 0.5, px: 0.5, py: 0.1, whiteSpace: 'nowrap' }}>
                            {k}{n > 1 ? `×${n}` : ''}
                        </Box>
                    ))}
                    {groupEntries.length > 6 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>+{groupEntries.length - 6}</Typography>
                    )}
                </Box>
                {totalLatency > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                        {totalLatency < 1000 ? `${totalLatency}ms` : `${(totalLatency / 1000).toFixed(1)}s`}
                    </Typography>
                )}
                <IconChevronDown size={13} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', opacity: 0.6 }} />
            </Box>
            {open && (
                <Box sx={{ bgcolor: '#fff' }}>
                    {trace.map((t, i) => <ToolRow key={i} t={t} />)}
                </Box>
            )}
        </Box>
    );
};

const Welcome = ({ isOrchestrator, agentName, onSample }) => {
    const samples = isOrchestrator
        ? ['List các project hiện có của tôi', 'Check Celery queue depth', 'Tạo project mới tên Test-1234, quota 5GB', 'Cách upload dataset YOLO format?']
        : [`${agentName} có thể giúp gì?`, 'Giải thích kiến trúc module này', 'Best practices khi triển khai feature'];
    return (
        <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', textAlign: 'center', pt: 8 }}>
            <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: '50%', bgcolor: 'primary.main', mb: 2 }}><IconRobot size={32} color="white" /></Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, fontSize: 28 }}>{isOrchestrator ? 'DataHub Agent Hub' : agentName}</Typography>
            <Typography color="text.secondary" sx={{ mb: 4, fontSize: 15, maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}>
                {isOrchestrator ? 'Tôi điều phối 19 sub-agent + backend API.' : 'Sub-agent chuyên môn — chat advisory.'}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, maxWidth: 600, mx: 'auto' }}>
                {samples.map((s) => (
                    <Button key={s} variant="outlined" onClick={() => onSample(s)}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', color: 'text.primary', borderColor: 'grey.300', py: 1.2, px: 1.5, fontSize: 13.5, '&:hover': { bgcolor: 'grey.50' } }}>
                        {s}
                    </Button>
                ))}
            </Box>
        </Box>
    );
};

// ============ SSE parser ============
async function streamChat({ agent_name, message, conversation_id, onText, onToolUse, onToolResult, onDone, onError, onStart }) {
    const baseUrl = (process.env.REACT_APP_API_URL || '/api/v1');
    const res = await fetch(`${baseUrl}/agent-hub/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
        body: JSON.stringify({ agent_name, message, conversation_id })
    });
    if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        throw new Error(`Rate limit. Reset in ${data?.detail?.reset_in_seconds || '?'}s`);
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    if (!res.body) throw new Error('No body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        while (true) {
            const idx = buf.indexOf('\n\n');
            if (idx < 0) break;
            const block = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let event = 'message';
            let data = null;
            for (const line of block.split('\n')) {
                if (line.startsWith('event:')) event = line.slice(6).trim();
                else if (line.startsWith('data:')) {
                    try { data = JSON.parse(line.slice(5).trim()); } catch { data = line.slice(5).trim(); }
                }
            }
            if (data == null) continue;
            if (event === 'start' && onStart) onStart(data);
            else if (event === 'text' && onText) onText(data.delta || '');
            else if (event === 'tool_use' && onToolUse) onToolUse(data);
            else if (event === 'tool_result' && onToolResult) onToolResult(data);
            else if (event === 'done' && onDone) onDone(data);
            else if (event === 'error' && onError) onError(data);
        }
    }
}

// ============ Main ============
export default function AgentHubPage() {
    const [agents, setAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState(0);
    const [conversations, setConversations] = useState([]);
    const [conversationId, setConversationId] = useState(() => localStorage.getItem(CONVERSATION_KEY) || null);
    const [rateLimit, setRateLimit] = useState(null);
    const [streamingMsg, setStreamingMsg] = useState(null);
    const [pastes, setPastes] = useState([]); // Pasted long-text attachments
    const [previewPaste, setPreviewPaste] = useState(null); // For modal preview
    const [images, setImages] = useState([]); // Pasted/uploaded images
    const [uploadingImage, setUploadingImage] = useState(false);
    // Queue cho message submit khi agent đang xử lý (auto-fire khi stream xong)
    const [pendingQueue, setPendingQueue] = useState([]);
    const sendMessageRef = useRef(null); // ref tới sendMessage để useEffect dùng (tránh stale closure)
    const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_COUNT);
    const [compacting, setCompacting] = useState(false);
    // Sau compact: carry-over summary làm context cho LẦN gửi message kế tiếp (1-shot)
    const [pendingContextPrefix, setPendingContextPrefix] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        api.get('/agent-hub/agents').then((res) => {
            if (!mounted) return;
            const list = res.data || [];
            setAgents(list);
            const master = list.find((a) => a.name === ORCHESTRATOR);
            setSelectedAgent(master || list[0] || null);
        }).catch((e) => mounted && setError(e?.message || 'Cannot load agents'));
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, streamingMsg, loading]);

    // Auto-fire pending queue khi stream cũ xong
    useEffect(() => {
        if (!loading && pendingQueue.length > 0 && sendMessageRef.current) {
            const next = pendingQueue[0];
            setPendingQueue((q) => q.slice(1));
            // Fire next với override bundle (bypass queue logic + clear logic)
            setTimeout(() => sendMessageRef.current?.(next.text, {
                text: next.text,
                pastes: next.pastes,
                images: next.images
            }), 50);
        }
    }, [loading, pendingQueue]);

    // Persist conversation_id
    useEffect(() => {
        if (conversationId) localStorage.setItem(CONVERSATION_KEY, conversationId);
        else localStorage.removeItem(CONVERSATION_KEY);
    }, [conversationId]);

    const loadConversations = useCallback(async () => {
        try {
            const res = await api.get('/agent-hub/conversations', { params: { limit: 50 } });
            setConversations(res.data || []);
        } catch (e) {
            console.warn('Cannot load conversations', e);
        }
    }, []);

    const newConversation = () => {
        setMessages([]);
        setStreamingMsg(null);
        setConversationId(null);
        setError(null);
        setVisibleCount(DEFAULT_VISIBLE_COUNT);
        setPendingContextPrefix(null);
        setPendingQueue([]);
    };

    // Compact: gọi backend tóm tắt → replace old messages bằng 1 summary
    const handleCompact = async () => {
        if (!conversationId || compacting) return;
        if (!window.confirm(
            `Tóm tắt ${messages.length} messages thành 1 summary?\n\nĐiều này sẽ:\n- Giữ ${Math.min(5, messages.length)} message gần nhất nguyên\n- Replace phần trước bằng 1 summary tự động (gọi Claude)\n- Cost ~$0.02-0.05`
        )) return;
        setCompacting(true);
        try {
            const res = await api.post(`/agent-hub/conversations/${conversationId}/compact`, {
                keep_recent: 5
            });
            const summary = res.data?.summary || '(summary empty)';
            const kept = messages.slice(-(res.data?.keep_recent || 5));
            setMessages([
                {
                    role: 'assistant',
                    agent: 'system',
                    content: `### 📋 Conversation Summary (compacted)\n\n${summary}\n\n---\n\n*${res.data?.original_count || 0} messages cũ đã được tóm tắt. Bên dưới là ${kept.length} message gần nhất.*`,
                    historical: true
                },
                ...kept
            ]);
            setVisibleCount(DEFAULT_VISIBLE_COUNT);
            // Carry-over summary làm context cho lần chat kế tiếp (1-shot)
            // → Agent SẼ biết context dù SDK session đã clear
            setPendingContextPrefix(summary);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Compact failed');
        } finally {
            setCompacting(false);
        }
    };

    // Update ref mỗi render — useEffect dùng ref tránh stale closure
    useEffect(() => {
        sendMessageRef.current = sendMessage;
    });

    // ─── State persistence: save snapshot khi state đổi ─────────────────
    // Restore khi mount (cứu user khi refresh giữa stream)
    useEffect(() => {
        if (!conversationId) return;
        const key = `${STATE_KEY_PREFIX}${conversationId}`;
        const snapshot = {
            messages: messages.slice(-MAX_PERSISTED_MESSAGES),
            streamingMsg,
            pendingQueue: pendingQueue.map(({ images, ...rest }) => ({
                ...rest,
                // KHÔNG persist blob URLs (mất sau reload)
                images: (images || []).map(({ previewUrl, ...meta }) => meta)
            })),
            pendingContextPrefix,
            input,
            ts: Date.now()
        };
        try {
            localStorage.setItem(key, JSON.stringify(snapshot));
        } catch (e) {
            // QuotaExceeded — bỏ qua, không block UX
        }
    }, [messages, streamingMsg, pendingQueue, pendingContextPrefix, input, conversationId]);

    // Restore từ localStorage khi conversation_id đầu tiên set
    const restoredRef = useRef(new Set());
    useEffect(() => {
        if (!conversationId || restoredRef.current.has(conversationId)) return;
        restoredRef.current.add(conversationId);
        const key = `${STATE_KEY_PREFIX}${conversationId}`;
        try {
            const saved = localStorage.getItem(key);
            if (!saved) return;
            const data = JSON.parse(saved);
            // Chỉ restore nếu state hiện tại "rỗng" (mới reload, chưa load gì)
            if (messages.length === 0 && data.messages?.length) {
                setMessages(data.messages);
            }
            if (!input && data.input) setInput(data.input);
            if (data.pendingContextPrefix && !pendingContextPrefix) {
                setPendingContextPrefix(data.pendingContextPrefix);
            }
            // Streaming msg = chỉ dấu agent đang chạy lúc bạn refresh
            // → check history backend để lấy final reply (nếu đã done)
            if (data.streamingMsg && !streamingMsg) {
                setStreamingMsg({ ...data.streamingMsg, _stale: true });
                // Sau 3s fetch history để kiểm tra agent đã trả lời chưa
                setTimeout(async () => {
                    try {
                        const res = await api.get('/agent-hub/history', {
                            params: { conversation_id: conversationId, limit: 3 }
                        });
                        const rows = res.data || [];
                        // History trả ascending, lấy dòng cuối có reply
                        const latest = rows.length > 0 ? rows[rows.length - 1] : null;
                        if (latest && latest.reply && new Date(latest.created_at).getTime() > data.ts) {
                            // Agent đã hoàn thành sau khi bạn refresh → append vào messages
                            setMessages((m) => [...m, {
                                role: 'assistant',
                                agent: latest.agent_name,
                                content: latest.reply,
                                usage: {
                                    cost_usd: latest.cost_usd,
                                    duration_ms: latest.duration_ms,
                                    num_turns: latest.num_turns
                                },
                                historical: true,
                                _recoveredAfterRefresh: true
                            }]);
                        }
                    } catch (e) {
                        console.warn('Cannot fetch history for stale recovery', e);
                    } finally {
                        setStreamingMsg(null);
                    }
                }, 3000);
            }
        } catch (e) {
            console.warn('Cannot restore state', e);
        }
    }, [conversationId]);

    // Cleanup localStorage entries cũ (>7 ngày) — chạy 1 lần khi mount component
    useEffect(() => {
        try {
            const now = Date.now();
            const WEEK = 7 * 24 * 3600 * 1000;
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith(STATE_KEY_PREFIX)) {
                    try {
                        const d = JSON.parse(localStorage.getItem(k));
                        if (d?.ts && now - d.ts > WEEK) localStorage.removeItem(k);
                    } catch {
                        localStorage.removeItem(k);
                    }
                }
            }
        } catch {}
    }, []);

    const restoreConversation = async (cid) => {
        try {
            setLoading(true);
            const res = await api.get('/agent-hub/history', { params: { conversation_id: cid, limit: 200 } });
            const rows = res.data || [];
            if (rows.length === 0) {
                setError('Conversation trống');
                return;
            }
            // Match agent
            const agentName = rows[0].agent_name;
            const agent = agents.find((a) => a.name === agentName);
            if (agent) setSelectedAgent(agent);
            // Build messages
            const msgs = [];
            for (const r of rows) {
                msgs.push({ role: 'user', content: r.message });
                msgs.push({
                    role: 'assistant',
                    agent: r.agent_name,
                    content: r.reply || '(no reply)',
                    tool_trace: [],  // tool_calls stored as JSON but not loaded into trace shape for simplicity
                    usage: { cost_usd: r.cost_usd, duration_ms: r.duration_ms, num_turns: r.num_turns },
                    historical: true
                });
            }
            setMessages(msgs);
            setConversationId(cid);
            setDrawerOpen(false);
        } catch (e) {
            setError(e?.message || 'Cannot restore conversation');
        } finally {
            setLoading(false);
        }
    };

    const sendMessage = async (text, overrideBundle = null) => {
        // overrideBundle = { text, pastes, images } khi flush từ pendingQueue
        const useText = overrideBundle?.text ?? text;
        const usePastes = overrideBundle?.pastes ?? pastes;
        const useImages = overrideBundle?.images ?? images;
        const hasPastes = usePastes.length > 0;
        const hasImages = useImages.length > 0;
        if ((!useText.trim() && !hasPastes && !hasImages) || !selectedAgent) return;

        // Nếu đang loading + không phải override (= user submit khi stream chạy)
        // → push vào queue, KHÔNG send ngay
        if (loading && !overrideBundle) {
            setPendingQueue((q) => [...q, {
                id: Date.now() + Math.random(),
                text: useText,
                pastes: usePastes,
                images: useImages,
                ts: Date.now()
            }]);
            setInput('');
            setPastes([]);
            setImages([]);
            return;
        }
        // Generate conversation_id nếu chưa có
        let cid = conversationId;
        if (!cid) {
            cid = newConvId();
            setConversationId(cid);
        }

        // Build message: short display + full content gửi backend
        const displayText = useText;
        let fullMessage = useText;
        if (hasPastes) {
            const parts = [useText];
            usePastes.forEach((p, i) => {
                parts.push(
                    `\n\n[Pasted text #${i + 1} — ${p.lines} lines, ${p.chars} chars]\n${p.content}\n[End paste #${i + 1}]`
                );
            });
            fullMessage = parts.join('');
        }
        // Prepend image attachment refs (agent dùng Read tool đọc)
        if (hasImages) {
            const imgLines = useImages.map((img, i) =>
                `[Attached image #${i + 1}: ${img.path} — ${img.mime}, ${img.size} bytes. Dùng Read tool đọc file path này để xem ảnh]`
            ).join('\n');
            fullMessage = `${fullMessage}\n\n${imgLines}`;
        }
        // Prepend carry-over context (sau compact, 1-shot)
        if (pendingContextPrefix) {
            fullMessage =
                `[CONTEXT FROM PREVIOUS COMPACTED CONVERSATION — bạn TIẾP TỤC phiên này, đừng coi là khởi đầu mới]\n` +
                `${pendingContextPrefix}\n` +
                `[END CONTEXT]\n\n` +
                `[Câu hỏi mới của user]\n${fullMessage}`;
            setPendingContextPrefix(null);
        }

        setMessages((m) => [...m, {
            role: 'user',
            content: displayText,
            pastes: hasPastes ? usePastes.map(({ content, ...meta }) => ({ ...meta, content })) : undefined,
            images: hasImages ? useImages.map(({ previewUrl, ...meta }) => ({ ...meta, previewUrl })) : undefined
        }]);
        if (!overrideBundle) {
            // Chỉ clear inputs khi user submit trực tiếp (override = flush từ queue, đã clear sẵn lúc enqueue)
            setInput('');
            setPastes([]);
            setImages([]);
        }
        setLoading(true);
        setStreamingMsg({ role: 'assistant', agent: selectedAgent.name, content: '', tool_trace: [] });

        try {
            await streamChat({
                agent_name: selectedAgent.name,
                message: fullMessage,
                conversation_id: cid,
                onStart: (d) => { if (d?.rate_limit) setRateLimit(d.rate_limit); },
                onText: (delta) => setStreamingMsg((cur) => ({ ...(cur || {}), content: (cur?.content || '') + delta })),
                onToolUse: (d) => setStreamingMsg((cur) => ({
                    ...(cur || {}),
                    tool_trace: [...(cur?.tool_trace || []), { id: d.id, tool: d.tool, input: d.input, output_preview: '(pending)', success: false }]
                })),
                onToolResult: (d) => setStreamingMsg((cur) => {
                    const trace = (cur?.tool_trace || []).map((t) => t.id === d.id ? { ...t, output_preview: d.output_preview, success: d.success } : t);
                    return { ...(cur || {}), tool_trace: trace };
                }),
                onDone: (d) => {
                    setStreamingMsg((cur) => {
                        const final = { ...(cur || {}), usage: d?.usage, tool_trace: cur?.tool_trace || [] };
                        setMessages((m) => [...m, final]);
                        return null;
                    });
                },
                onError: (d) => { setError(d?.detail || 'Stream error'); setStreamingMsg(null); }
            });
        } catch (e) {
            setError(e?.message || 'Chat failed');
            setStreamingMsg(null);
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const selectAgent = (a) => {
        setSelectedAgent(a);
        // Đổi agent → reset conversation (1 conversation = 1 agent)
        newConversation();
        setDrawerOpen(false);
    };

    const orchestrator = useMemo(() => agents.find((a) => a.name === ORCHESTRATOR), [agents]);
    const maintAgents = useMemo(() => agents.filter((a) => a.tier === 'maintenance' && a.name !== ORCHESTRATOR), [agents]);
    const devAgents = useMemo(() => agents.filter((a) => a.tier === 'development'), [agents]);
    const isOrchestrator = selectedAgent?.name === ORCHESTRATOR;

    const renderAssistantBody = (m) => (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 1, bgcolor: 'primary.main', color: 'white' }}><IconRobot size={16} /></Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{m.agent}</Typography>
                {m.historical && <Chip label="lịch sử" size="small" sx={{ height: 18, fontSize: 10 }} />}
            </Box>
            <Box sx={{ pl: 4.5, '& > *:first-of-type': { mt: 0 } }}>
                <Md>{m.content}</Md>
                {m.tool_trace?.length > 0 && <ToolTrace trace={m.tool_trace} />}
                {m.usage && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1.5, color: 'text.secondary', fontSize: 11.5 }}>
                        {m.usage.cost_usd > 0 && <span>${m.usage.cost_usd.toFixed(4)}</span>}
                        {m.usage.num_turns > 0 && <span>· {m.usage.num_turns} turn{m.usage.num_turns > 1 ? 's' : ''}</span>}
                        {m.usage.duration_ms > 0 && <span>· {(m.usage.duration_ms / 1000).toFixed(1)}s</span>}
                    </Box>
                )}
            </Box>
        </>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 88px)', bgcolor: '#fff' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'grey.200', minHeight: 56 }}>
                <IconButton size="small" onClick={() => { setDrawerOpen(true); if (drawerTab === 1) loadConversations(); }}>
                    <IconLayoutSidebar size={20} />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconRobot size={18} color="#6366f1" />
                    <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{selectedAgent?.name || 'Agent Hub'}</Typography>
                    {isOrchestrator && <Chip label="master" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />}
                    {selectedAgent && !isOrchestrator && (
                        <Chip label={selectedAgent.tier} size="small" color={selectedAgent.tier === 'maintenance' ? 'warning' : 'info'} sx={{ height: 18, fontSize: 10 }} />
                    )}
                    {conversationId && (
                        <Tooltip title={`Conversation ID: ${conversationId.slice(0, 8)}…`}>
                            <Chip
                                icon={<IconMessage size={12} />}
                                label={`${messages.filter((m) => m.role === 'user').length} msg`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: 11 }}
                            />
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ flex: 1 }} />
                {rateLimit && (
                    <Tooltip title={`Reset trong ${Math.round(rateLimit.reset_in_seconds || 0)}s`}>
                        <Chip
                            label={`${rateLimit.remaining ?? '?'}/${rateLimit.limit} chat`}
                            size="small"
                            color={rateLimit.remaining > 10 ? 'default' : 'warning'}
                            sx={{ height: 22, fontSize: 11 }}
                        />
                    </Tooltip>
                )}
                {pendingQueue.length > 0 && (
                    <Tooltip title={`${pendingQueue.length} message đang đợi gửi sau khi agent xong. Click để huỷ queue.`}>
                        <Chip
                            label={`⏳ ${pendingQueue.length} pending`}
                            size="small"
                            color="info"
                            onDelete={() => setPendingQueue([])}
                            deleteIcon={<IconX size={14} />}
                            sx={{ height: 22, fontSize: 11 }}
                        />
                    </Tooltip>
                )}
                <Tooltip title="Hội thoại mới">
                    <IconButton size="small" onClick={newConversation}>
                        <IconPlus size={18} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Drawer */}
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 340, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Agent Hub</Typography>
                            <Typography variant="caption" color="text.secondary">{agents.length} agents · Claude Max</Typography>
                        </Box>
                        <Tooltip title="Hội thoại mới">
                            <IconButton size="small" color="primary" onClick={() => { newConversation(); setDrawerOpen(false); }}>
                                <IconPlus size={18} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Tabs value={drawerTab} onChange={(_, v) => { setDrawerTab(v); if (v === 1) loadConversations(); }} variant="fullWidth">
                        <Tab icon={<IconUsersGroup size={16} />} iconPosition="start" label="Agents" sx={{ minHeight: 40, fontSize: 12 }} />
                        <Tab icon={<IconHistory size={16} />} iconPosition="start" label={
                            <Badge color="primary" badgeContent={conversations.length} max={99}>
                                <Box sx={{ pr: 1.5 }}>Hội thoại</Box>
                            </Badge>
                        } sx={{ minHeight: 40, fontSize: 12 }} />
                    </Tabs>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {drawerTab === 0 && (
                            <>
                                {orchestrator && (
                                    <>
                                        <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 600 }}>🎯 Master</ListSubheader>
                                        <List dense disablePadding>
                                            <ListItemButton selected={selectedAgent?.name === orchestrator.name} onClick={() => selectAgent(orchestrator)}>
                                                <ListItemText primary={<Typography variant="body2" fontWeight={600}>{orchestrator.name}</Typography>} secondary={<Typography variant="caption" color="text.secondary">Orchestrator</Typography>} />
                                            </ListItemButton>
                                        </List>
                                        <Divider />
                                    </>
                                )}
                                <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 600 }}>🔧 Maintenance ({maintAgents.length})</ListSubheader>
                                <List dense disablePadding>
                                    {maintAgents.map((a) => (
                                        <ListItemButton key={a.name} selected={selectedAgent?.name === a.name} onClick={() => selectAgent(a)}>
                                            <ListItemText primary={<Typography variant="body2">{a.name}</Typography>} secondary={<Typography variant="caption" color="text.secondary" noWrap>{a.description}</Typography>} />
                                        </ListItemButton>
                                    ))}
                                </List>
                                <Divider />
                                <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 600 }}>💻 Development ({devAgents.length})</ListSubheader>
                                <List dense disablePadding>
                                    {devAgents.map((a) => (
                                        <ListItemButton key={a.name} selected={selectedAgent?.name === a.name} onClick={() => selectAgent(a)}>
                                            <ListItemText primary={<Typography variant="body2">{a.name}</Typography>} secondary={<Typography variant="caption" color="text.secondary" noWrap>{a.description}</Typography>} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </>
                        )}
                        {drawerTab === 1 && (
                            <List dense disablePadding>
                                {conversations.length === 0 && (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <IconMessage size={32} style={{ opacity: 0.3 }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                            Chưa có hội thoại nào.
                                        </Typography>
                                    </Box>
                                )}
                                {conversations.map((c) => (
                                    <ListItemButton
                                        key={c.conversation_id}
                                        selected={c.conversation_id === conversationId}
                                        onClick={() => restoreConversation(c.conversation_id)}
                                        sx={{ borderBottom: '1px solid', borderColor: 'grey.100', alignItems: 'flex-start', py: 1.25 }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" sx={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.4, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {c.first_message}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                                                    <Chip label={c.agent_name} size="small" sx={{ height: 18, fontSize: 10 }}
                                                          color={c.agent_name === ORCHESTRATOR ? 'primary' : (c.agent_name.startsWith('datahub-') ? 'warning' : 'info')}
                                                          variant="outlined" />
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                                        {c.message_count} msg · {relTime(c.last_message_at)}
                                                    </Typography>
                                                    {c.total_cost_usd > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                                            · ${c.total_cost_usd.toFixed(3)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
            </Drawer>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2, maxWidth: MAX_WIDTH, mx: 'auto', width: '90%' }}>{error}</Alert>
            )}

            {/* Chat */}
            <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', px: 2, py: 3 }}>
                {messages.length === 0 && !streamingMsg && selectedAgent && (
                    <Welcome isOrchestrator={isOrchestrator} agentName={selectedAgent.name} onSample={(s) => sendMessage(s)} />
                )}
                <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto' }}>
                    {/* Hide older messages — chỉ render N gần nhất để giảm lag */}
                    {messages.length > visibleCount && (
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setVisibleCount((c) => c + VISIBLE_INCREMENT)}
                                sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
                            >
                                ↑ Hiện {Math.min(VISIBLE_INCREMENT, messages.length - visibleCount)} tin nhắn cũ hơn
                                <Box component="span" sx={{ ml: 1, opacity: 0.6 }}>
                                    ({messages.length - visibleCount} đang ẩn · {messages.length} tổng)
                                </Box>
                            </Button>
                            <Tooltip title="Tóm tắt toàn bộ conversation thành 1 summary (gọi Claude). Old messages sẽ thu gọn.">
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleCompact}
                                    disabled={compacting}
                                    sx={{ textTransform: 'none', fontSize: 12, ml: 1, color: 'primary.main' }}
                                >
                                    {compacting ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : '✦'} Compact
                                </Button>
                            </Tooltip>
                        </Box>
                    )}
                    {messages.slice(-visibleCount).map((m, i) => {
                        if (m.role === 'user') {
                            return (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <Box sx={{ maxWidth: '85%', bgcolor: 'grey.100', borderRadius: 3, px: 2, py: 1.25 }}>
                                        {m.content && (
                                            <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.6 }}>{m.content}</Typography>
                                        )}
                                        {m.pastes?.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: m.content ? 0.75 : 0 }}>
                                                {m.pastes.map((p, pi) => (
                                                    <Chip
                                                        key={pi}
                                                        icon={<IconClipboard size={12} />}
                                                        label={`Pasted #${pi + 1} · +${p.lines} lines`}
                                                        onClick={() => setPreviewPaste(p)}
                                                        size="small"
                                                        sx={{ fontSize: 11, cursor: 'pointer', bgcolor: 'white' }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        {m.images?.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: m.content || m.pastes?.length ? 0.75 : 0 }}>
                                                {m.images.map((img, ii) => (
                                                    <Box
                                                        key={ii}
                                                        component="img"
                                                        src={img.previewUrl}
                                                        alt={img.filename}
                                                        sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        }
                        return <Box key={i} sx={{ mb: 4 }}>{renderAssistantBody(m)}</Box>;
                    })}

                    {streamingMsg && (
                        <Box sx={{ mb: 4 }}>
                            {streamingMsg._stale && (
                                <Alert severity="info" icon={<CircularProgress size={14} />} sx={{ mb: 1.5, py: 0.5 }}>
                                    <Typography variant="caption">
                                        Đang khôi phục tiến trình từ phiên trước (refresh)... Sẽ kiểm tra backend trong vài giây.
                                    </Typography>
                                </Alert>
                            )}
                            {renderAssistantBody(streamingMsg)}
                            {!streamingMsg._stale && (
                                <Box sx={{ pl: 4.5, mt: 0.5 }}>
                                    <Box sx={{ display: 'inline-block', width: 8, height: 14, bgcolor: 'primary.main', animation: 'blink 1s infinite' }} />
                                    <style>{`@keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }`}</style>
                                </Box>
                            )}
                        </Box>
                    )}

                    {loading && !streamingMsg && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, pl: 4.5 }}>
                            <CircularProgress size={14} thickness={5} />
                            <Typography variant="caption" color="text.secondary">{isOrchestrator ? 'Master đang khởi tạo…' : 'Đang khởi tạo…'}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Input */}
            {selectedAgent && (
                <Box sx={{ borderTop: '1px solid', borderColor: 'grey.200', bgcolor: '#fff', py: 2 }}>
                    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', px: 2 }}>
                        {/* Image attachment chips */}
                        {(images.length > 0 || uploadingImage) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                                {images.map((img, i) => (
                                    <Box key={img.id} sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Box
                                            component="img"
                                            src={img.previewUrl}
                                            alt={img.filename}
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'grey.300',
                                                bgcolor: 'grey.50'
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => setImages((arr) => arr.filter((x) => x.id !== img.id))}
                                            sx={{
                                                position: 'absolute', top: -6, right: -6,
                                                width: 18, height: 18, p: 0,
                                                bgcolor: 'background.paper', border: '1px solid', borderColor: 'grey.300',
                                                '&:hover': { bgcolor: 'error.50' }
                                            }}
                                        >
                                            <IconX size={10} />
                                        </IconButton>
                                    </Box>
                                ))}
                                {uploadingImage && (
                                    <Box sx={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed', borderColor: 'grey.300', borderRadius: 1 }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {/* Paste attachment chips */}
                        {pastes.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                                {pastes.map((p, i) => (
                                    <Chip
                                        key={p.id}
                                        icon={<IconClipboard size={12} />}
                                        label={`Pasted #${i + 1} · +${p.lines} lines · ${p.chars >= 1000 ? `${(p.chars / 1000).toFixed(1)}K` : p.chars} chars`}
                                        onDelete={() => setPastes((ps) => ps.filter((x) => x.id !== p.id))}
                                        onClick={() => setPreviewPaste(p)}
                                        deleteIcon={<IconX size={14} />}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            fontSize: 11,
                                            cursor: 'pointer',
                                            bgcolor: 'grey.50',
                                            '&:hover': { bgcolor: 'grey.100' }
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, border: '1px solid', borderColor: 'grey.300', borderRadius: 3, px: 1.5, py: 1, '&:focus-within': { borderColor: 'primary.main' } }}>
                            <TextField
                                inputRef={inputRef} fullWidth multiline maxRows={8}
                                placeholder={
                                    loading
                                        ? 'Bạn có thể gõ câu hỏi tiếp theo trong khi agent đang xử lý…'
                                        : isOrchestrator
                                        ? 'Hỏi master agent… (paste content dài tự động rút gọn)'
                                        : `Chat với ${selectedAgent.name}…`
                                }
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                                onPaste={async (e) => {
                                    // Detect image trong clipboard trước (vd Screenshot, copy-from-browser)
                                    const items = Array.from(e.clipboardData?.items || []);
                                    const imageItems = items.filter((it) => it.type && it.type.startsWith('image/'));
                                    if (imageItems.length > 0) {
                                        e.preventDefault();
                                        setUploadingImage(true);
                                        try {
                                            for (const it of imageItems) {
                                                const blob = it.getAsFile();
                                                if (!blob) continue;
                                                const fd = new FormData();
                                                const ext = (it.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
                                                fd.append('file', blob, `pasted_${Date.now()}.${ext}`);
                                                const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
                                                const res = await fetch(`${baseUrl}/agent-hub/upload-attachment`, {
                                                    method: 'POST',
                                                    headers: { Authorization: getAuthHeader() },
                                                    body: fd
                                                });
                                                if (!res.ok) throw new Error(`Upload failed HTTP ${res.status}`);
                                                const data = await res.json();
                                                // Create preview URL từ blob để hiển thị chip
                                                const previewUrl = URL.createObjectURL(blob);
                                                setImages((imgs) => [...imgs, {
                                                    id: Date.now() + Math.random(),
                                                    path: data.path,
                                                    filename: data.filename,
                                                    size: data.size_bytes,
                                                    mime: data.mime_type,
                                                    previewUrl
                                                }]);
                                            }
                                        } catch (err) {
                                            setError(err?.message || 'Image upload failed');
                                        } finally {
                                            setUploadingImage(false);
                                        }
                                        return;
                                    }
                                    // Fallback text paste
                                    const text = e.clipboardData.getData('text') || '';
                                    if (!text) return;
                                    const lines = (text.match(/\n/g) || []).length + 1;
                                    if (lines >= PASTE_THRESHOLD_LINES || text.length >= PASTE_THRESHOLD_CHARS) {
                                        e.preventDefault();
                                        setPastes((ps) => [...ps, {
                                            id: Date.now() + Math.random(),
                                            lines,
                                            chars: text.length,
                                            preview: text.slice(0, 120).replace(/\s+/g, ' '),
                                            content: text
                                        }]);
                                    }
                                }}
                                disabled={false}
                                variant="standard"
                                InputProps={{ disableUnderline: true, sx: { fontSize: 15, lineHeight: 1.5 } }}
                            />
                            <IconButton onClick={() => sendMessage(input)} disabled={!input.trim() && pastes.length === 0 && images.length === 0}
                                sx={{ bgcolor: (input.trim() || pastes.length > 0 || images.length > 0) ? 'primary.main' : 'grey.300', color: 'white', width: 32, height: 32, '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' } }}>
                                {loading ? <CircularProgress size={14} color="inherit" /> : <IconSend size={16} />}
                            </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center', fontSize: 11 }}>
                            Streaming · Enter để gửi · Shift+Enter xuống dòng · Powered by Claude Max
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Paste preview dialog */}
            <Dialog open={!!previewPaste} onClose={() => setPreviewPaste(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 15 }}>
                    <IconClipboard size={18} />
                    Pasted content
                    {previewPaste && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                            {previewPaste.lines} lines · {previewPaste.chars} chars
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        component="pre"
                        sx={{
                            m: 0,
                            p: 1.5,
                            bgcolor: '#fafbfc',
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: 1,
                            fontSize: 12,
                            fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
                            maxHeight: '60vh',
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {previewPaste?.content || ''}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewPaste(null)}>Đóng</Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (previewPaste?.content) navigator.clipboard.writeText(previewPaste.content);
                        }}
                    >
                        Copy
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
